import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { trackEvent } from "@/lib/analytics";

/**
 * Wrap any premium-only component:
 *
 *   <PremiumGate feature="Kick Counter" description="Track baby kicks ...">
 *     <KickCounterCard />
 *   </PremiumGate>
 *
 * Free users see a self-contained gold-bordered card with a lock icon, the
 * feature name, a one-line description, and an "Upgrade to Premium" CTA that
 * routes to /settings?tab=subscription. Premium users get the raw children.
 *
 * Subscription status comes from AuthContext (single source of truth, single
 * /api/subscription/status fetch on login). While the status is unknown we
 * render a neutral skeleton sized like the gate card so neither premium nor
 * free users see a flash of mis-matched content.
 */
const PremiumGate = ({ feature, description, children }) => {
  const { token, subscription, subscriptionLoading } = useAuth();
  const slug = feature.toLowerCase().replace(/\s+/g, "-");

  // Unknown auth -> treat as free immediately (no token = not signed in).
  // Status still fetching for an authenticated user -> render a skeleton so
  // we don't flash either the premium content or the lock card.
  if (token && (subscriptionLoading || subscription === null)) {
    return (
      <div
        className="rounded-3xl border border-white/5 bg-mystic-900/30 p-8 animate-pulse"
        data-testid={`premium-gate-${slug}-skeleton`}
        aria-busy="true"
      >
        <div className="w-14 h-14 mx-auto rounded-full bg-white/5 mb-4" />
        <div className="h-3 w-24 mx-auto rounded bg-white/5 mb-3" />
        <div className="h-5 w-48 mx-auto rounded bg-white/10 mb-3" />
        <div className="h-3 w-64 mx-auto rounded bg-white/5 mb-2" />
        <div className="h-3 w-56 mx-auto rounded bg-white/5 mb-5" />
        <div className="h-10 w-44 mx-auto rounded-full bg-white/5" />
      </div>
    );
  }

  if (subscription?.is_premium) {
    return children;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid={`premium-gate-${slug}`}
      className="rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/15 via-yellow-500/5 to-amber-500/10 p-8 text-center backdrop-blur-md shadow-2xl"
    >
      <div className="w-14 h-14 mx-auto rounded-full bg-amber-400/20 flex items-center justify-center mb-4">
        <Lock className="w-6 h-6 text-amber-300" />
      </div>
      <p className="text-amber-200 text-xs uppercase tracking-widest mb-2">Premium feature</p>
      <h4 className="text-white font-semibold text-xl">{feature}</h4>
      {description && (
        <p className="text-mystic-200 text-sm mt-3 leading-relaxed max-w-md mx-auto">{description}</p>
      )}
      <Button
        asChild
        className="mt-5 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-amber-950 font-semibold rounded-full px-6 py-5 text-base"
        data-testid={`upgrade-btn-${slug}`}
      >
        <Link to="/settings?tab=subscription" onClick={() => trackEvent("upgrade_clicked", "premium_gate", { feature })}>
          <Crown className="w-4 h-4 mr-2" />
          Upgrade to Premium
        </Link>
      </Button>
    </motion.div>
  );
};

export default PremiumGate;
